/**
 * Learning Path API - Endpoints for user learning path progress
 */
import { AxiosError, AxiosResponse } from "axios";
import axiosHandler from "@/utils/axios-handler";
import { UserLearningPath } from "@/types/learning-paths";
import { academyApiUrl } from '@/lib/constants/api';

interface LearningPathResponse {
  data: unknown;
  [key: string]: unknown;
}

interface UserLessonProgress {
  uid: string;
  entryUid: string;
  progress: {
    isCompleted: boolean;
    hasStarted: boolean;
    percentage: number;
    completedAt: string | null;
  };
}

export interface LearningPathLessonsResponse {
  lessons: UserLessonProgress[];
  [key: string]: unknown;
}

const isAxiosError = (
  response: AxiosResponse | AxiosError
): response is AxiosError => {
  return (response as AxiosError).isAxiosError === true;
};

/** Fetch learning path details by UID */
export const fetchLearningPath = async (
  uid: string
): Promise<LearningPathResponse> => {
  const response = await axiosHandler({
    method: "GET",
    url: academyApiUrl(`/v1/users/learning-paths/${uid}`),
    headers: { "Content-Type": "application/json" },
    withCredentials: true,
  });

  if (isAxiosError(response)) {
    throw new Error(`Failed to fetch learning path: ${response.message}`);
  }

  return response.data;
};

/** Fetch user's course progress for a learning path */
export async function fetchLearningPathProgress(
  uid: string
): Promise<UserLearningPath> {
  const response = await axiosHandler({
    method: "GET",
    url: academyApiUrl(`/v1/users/learning-paths/${uid}/courses`),
    headers: { "Content-Type": "application/json" },
    withCredentials: true,
  });

  if (isAxiosError(response)) {
    if (response.response) {
      // 404 here means "not enrolled in this learning path" — an expected, common
      // state the caller (LearningPathSectionClient) already treats as such. Only
      // log the statuses that are actually unexpected.
      if (response.response.status !== 404) {
        console.error(
          "Error fetching learning path courses:",
          response.response.status,
          response.response.data
        );
      }
      throw new Error(
        `Failed to fetch learning path courses: ${response.response.status}`
      );
    } else if (response.request) {
      console.error("No response received:", response.request);
      throw new Error("No response received from server");
    } else {
      console.error("Error setting up request:", response.message);
      throw new Error("Failed to make request");
    }
  }

  return response.data;
}

/** Fetch user's lesson progress for a course */
export const fetchLearningPathLessons = async (
  learningPathId: string
): Promise<LearningPathLessonsResponse> => {
  const response = await axiosHandler({
    method: "GET",
    url: academyApiUrl(`/v1/users/courses/${learningPathId}/lessons`),
    headers: { "Content-Type": "application/json" },
    withCredentials: true,
  });

  if (isAxiosError(response)) {
    if (response.response) {
      console.error(
        "Error fetching learning path lessons:",
        response.response.status,
        response.response.data
      );
      throw new Error(
        `Failed to fetch learning path lessons: ${response.response.status}`
      );
    } else if (response.request) {
      console.error("No response received:", response.request);
      throw new Error("No response received from server");
    } else {
      console.error("Error setting up request:", response.message);
      throw new Error("Failed to make request");
    }
  }

  return response.data;
};
